/**
 * Shared sorting utility functions for lists and dropdowns
 * Centralizes sorting logic to avoid duplication across components
 */

/**
 * Sort items alphabetically by a specified key
 * @param {Array} items - Array of objects to sort
 * @param {string} key - Object key to sort by (default: "name")
 * @returns {Array} Sorted array
 */
export const sortAlphabetically = (items, key = "name") => {
  return [...items].sort((a, b) => 
    (a[key] || "").localeCompare(b[key] || "")
  );
};

/**
 * Sort employees by role first, then alphabetically by name
 * @param {Array} employees - Array of employee objects
 * @returns {Array} Sorted array
 */
export const sortEmployeesByRole = (employees) => {
  return [...employees].sort((a, b) => {
    const roleCompare = (a.role || "").localeCompare(b.role || "");
    if (roleCompare !== 0) return roleCompare;
    return (a.name || "").localeCompare(b.name || "");
  });
};

/**
 * Group employees by role for dropdown display
 * @param {Array} employees - Array of employee objects (should be pre-sorted)
 * @returns {Object} Object with roles as keys and employee arrays as values
 */
export const groupEmployeesByRole = (employees) => {
  const sorted = sortEmployeesByRole(employees);
  return sorted.reduce((acc, emp) => {
    const role = emp.role || "Other";
    if (!acc[role]) acc[role] = [];
    acc[role].push(emp);
    return acc;
  }, {});
};

/**
 * Sort materials by category first, then alphabetically by name
 * @param {Array} materials - Array of material objects
 * @returns {Array} Sorted array
 */
export const sortMaterialsByCategory = (materials) => {
  return [...materials].sort((a, b) => {
    const catCompare = (a.category || "").localeCompare(b.category || "");
    if (catCompare !== 0) return catCompare;
    return (a.name || "").localeCompare(b.name || "");
  });
};

/**
 * Group materials by category for dropdown display
 * @param {Array} materials - Array of material objects (should be pre-sorted)
 * @returns {Object} Object with categories as keys and material arrays as values
 */
export const groupMaterialsByCategory = (materials) => {
  const sorted = sortMaterialsByCategory(materials);
  return sorted.reduce((acc, mat) => {
    const category = mat.category || "Other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(mat);
    return acc;
  }, {});
};

/**
 * Sort a simple array of strings alphabetically
 * @param {Array} items - Array of strings
 * @returns {Array} Sorted array
 */
export const sortStrings = (items) => {
  return [...items].sort((a, b) => (a || "").localeCompare(b || ""));
};
